import debounce from 'lodash/debounce'
import { mapColorPalette, stateToRegionByName, ZOOM_LEVELS } from '../../../../../constants/Map'
import usStates from '../../../../../constants/Map/polygons/us-states.json'
import {
  ICountyPublicWaterSystemResult,
  IRegionPublicWaterSystemResult,
} from '../../../../../types'
import { getStateCountiesPolygon } from '../../../../../utils/mapHelper'
import { handleMoveEnd } from './handleMoveEnd'

type HandleZoomProps = {
  mapRef: any
  prevZoom: number
  prevSelectedCode: React.MutableRefObject<string | null | undefined>
  regionsViolationsRef: React.RefObject<IRegionPublicWaterSystemResult[]>
  countyViolationsRef: React.RefObject<ICountyPublicWaterSystemResult[]>
}

export const handleZoom = async ({
  mapRef,
  prevZoom,
  prevSelectedCode,
  regionsViolationsRef,
  countyViolationsRef,
}: HandleZoomProps) => {
  const map = mapRef.current

  if (!map) return

  const zoom = map.getZoom()

  const mapInstance = map as mapboxgl.Map & { _isZoomProcessing?: boolean }

  if (mapInstance._isZoomProcessing) return
  mapInstance._isZoomProcessing = true

  try {
    /*    if (zoom < ZOOM_LEVELS.STATE.MIN) {
          await handleRegionZoom(map, regionsViolationsRef);
        }
    
        if (isCountyZoomRange(zoom, prevZoom)) {
          await handleCountyZoom(map, prevSelectedCode, countyViolationsRef);
        }*/

    prevZoom = zoom
  } finally {
    setTimeout(() => {
      mapInstance._isZoomProcessing = false
    }, 100)
  }
}
// Handle State OrRegion Data Fetch
export const handleZoomEnd = async ({
  mapRef,
  prevZoom,
  prevSelectedCode,
  regionsViolationsRef,
  countyViolationsRef,
}) => {
  const map = mapRef.current
  const zoom = map.getZoom()

  if (zoom < ZOOM_LEVELS.STATE.MIN) {
    await handleRegionZoom(map, regionsViolationsRef)
  }
  if (isCountyZoomRange(zoom, prevZoom)) {
    await handleCountyZoom(map, prevSelectedCode, countyViolationsRef)
  }
  prevZoom = zoom
}

export const handleRegionZoom = async (
  map: mapboxgl.Map,
  regionsViolationsRef: React.RefObject<IRegionPublicWaterSystemResult[]>,
) => {
  try {
    const source = map.getSource('main')
    if (!source) return

    if (!map.getLayer('main')) return;
    (source as mapboxgl.GeoJSONSource).setData(usStates as GeoJSON.FeatureCollection)

    if (!regionsViolationsRef?.current?.length) {
      console.warn('regionsViolationsRef.current is empty or undefined')
      return
    }

    const regionToColorMapping = createRegionColorMapping(regionsViolationsRef.current)
    updateRegionColors(map, regionToColorMapping)
  } catch (error) {
    return
  }
}

export const handleCountyZoom = async (
  map: any,
  prevSelectedCode: React.MutableRefObject<string | null | undefined>,
  countyViolationsRef: React.RefObject<ICountyPublicWaterSystemResult[]>,
) => {
  try {
    const countiesGeo = await getStateCountiesPolygon(prevSelectedCode?.current || '')
    if (!countiesGeo) return

    const source = map?.getSource('main')
    if (!source) return

    if (!map.getLayer('main')) return
    ;(source as mapboxgl.GeoJSONSource).setData(countiesGeo)

    const countyColorMapping = createCountyColorMapping(countyViolationsRef?.current)
    if (countyColorMapping) {
      const matchExpression = [
        'match',
        ['get', 'name'],
        ...Object.entries(countyColorMapping)
          .map(([county, color]) => [county, color])
          .flat(),
        '#ffffff',
      ]

      map.setPaintProperty('main', 'fill-color', matchExpression)
    }
  } catch (error) {
    return
  }
}

const isCountyZoomRange = (zoom: number, prevZoom: number): boolean => {
  return (
    zoom < ZOOM_LEVELS.COUNTY.MAX &&
    zoom > ZOOM_LEVELS.COUNTY.MIN &&
    (prevZoom >= ZOOM_LEVELS.COUNTY.MAX || prevZoom <= ZOOM_LEVELS.COUNTY.MIN)
  )
}

const createRegionColorMapping = (
  regions: IRegionPublicWaterSystemResult[],
): Record<string, string> => {
  return regions.reduce(
    (acc, region, index) => {
      acc[region?.epaRegion] = mapColorPalette[index]
      return acc
    },
    {} as Record<string, string>,
  )
}

const createCountyColorMapping = (
  counties: ICountyPublicWaterSystemResult[] | undefined | null,
): Record<string, string> | null => {
  if (!counties?.length) return null

  return counties.reduce(
    (acc, county, index) => {
      if (county?.county_name) {
        const color = mapColorPalette[index] || '#ffffff'
        acc[county.county_name] = color
      }
      return acc
    },
    {} as Record<string, string>,
  )
}

const updateRegionColors = (map: mapboxgl.Map, regionToColorMapping: Record<string, string>) => {
  if (!stateToRegionByName || typeof stateToRegionByName !== 'object') {
    console.warn('stateToRegionByName is invalid:', stateToRegionByName)
    return
  }

  try {
    map.setPaintProperty('main', 'fill-color', [
      'match',
      ['get', 'name'],
      ...Object.entries(stateToRegionByName)
        .map(([stateName, { epaRegion }]) => {
          const color = regionToColorMapping[epaRegion] || '#ffffff'
          return [stateName, color]
        })
        .flat(),
      '#ffffff',
    ])
  } catch (error) {
    return
  }
}

export const debouncedHandleMoveEnd = debounce(handleMoveEnd, 250)
